import Store from 'store/store';
import { getDeviceInfo } from 'utils/bolt/client';
import { accessNested, parseDate, toQueryString } from 'utils/utils';
// import cached from 'utils/cached';
import stringWithParams from 'utils/stringWithParams';

export function buildClientStoreData(state) {
	Object.assign(state.client, getDeviceInfo());
	return state;
}

export function getValueForClientSize(variable = {}, client, options = {}) {
	if (typeof variable === 'string' || !variable) {
		return variable;
	}

	if (!client) {
		client = Store.get().client;
	}

	let {
		deviceSize = 'mobile',
		orientation = 'portrait'
	} = client;

	if (deviceSize === 'largeDesktop' || deviceSize === 'smallDesktop') deviceSize = 'desktop';

	let deviceOrientation = `${deviceSize}${orientation[0].toUpperCase() + orientation.slice(1)}`;

	if (options.nullIfDisabled) {
		// if you want to return nothing when the relevant item in thundr is disabled,
		// instead of using the fallback logic.
		// ex: don't show TT on mobile landscape if "mobileLanscape" is disabled in "variable", but use the next
		// available asset if "mobileLandscape" is an empty string.
		if (variable[deviceOrientation] === undefined && variable[deviceSize] === undefined) {
			return null;
		}
	}

	if (deviceOrientation === 'mobileLandscape' && !variable.mobileLandscape) {
		deviceOrientation = 'tabletLandscape';
	}

	if (deviceOrientation === 'tabletLandscape' && !variable.tabletLandscape) {
		deviceOrientation = 'desktopLandscape';
		deviceSize = 'desktop';
	}

	return variable[deviceOrientation] || variable[deviceSize] || variable.desktop || variable.tablet || variable.mobile;
}

const replaceVH = str => typeof str === 'string' ? str.replace(/\b(\d+)vh\b/g, 'calc($1 * var(--vh))') : str;

export function getSizeOverride(data) {
	const size = getValueForClientSize(data);
	if (!size) {
		return size;
	}
	if (typeof size === 'string') {
		return replaceVH(size);
	}
	return Object.fromEntries(
		Object.entries(size).map(([key, value]) => [key, replaceVH(value)])
	);
}

export function getTTSizeOverride(tt) {
	return getSizeOverride(tt?.titleTreatmentSize) || {};
}

export function normalizeRegion(region) {
	if (!region || typeof region !== 'string') {
		return null;
	}
	region = region.toUpperCase().replace(/^([A-Z]{2})_.+$/, '$1');
	return region.length === 2 ? region : null;
}

export function getPagePath(pageData) {
	let path = pageData?.path || '/';
	let hash = '';
	if (path.startsWith('#')) {
		hash = path;
		path = '/';
		if (pageData.section) {
			path = Store.get().pages?.boltHome?.path || '/';
		}
	}
	return { path, hash };
}

export function getRelativePageUrl(page, queryString = typeof window !== 'undefined' ? location.search : '', hashInPage) {
	let store = Store.get();
	let activePage = store.activePage;
	let newPageData = typeof page === 'string' ? store.appData?.pages?.[page] : page;
	if (!newPageData) {
		return null;
	}
	if (typeof queryString === 'object') {
		queryString = toQueryString(queryString);
	}
	// let qs = !ignoreQueryString && typeof window !== 'undefined' ? location.search : '';

	let { path, hash } = getPagePath(newPageData);
	if (!hash) {
		hash = hashInPage || '';
	}
	let currentPageData = accessNested(store, ['appData', 'pages', activePage]);
	if (!currentPageData) {
		return path + queryString + hash;
	}

	let relativeRoot = getPagePath(currentPageData).path.slice(1).split('/').slice(1).fill('../').join('');
	return (relativeRoot + path.slice(1) + queryString + hash) || './';
}

export function getPageIdFromURL(pages) {
	let url = location.pathname + location.hash;
	let top = Object.values(pages || Store.get().appData.pages || {}).map(page => {
		let path = page.path;
		if (page.baseHtml) {
			path = path?.startsWith('#') ? page.baseHtml + path : path + page.baseHtml;
		}
		if (!url.replace('preview/', '').includes(path)) {
			return null;
		}
		return { id: page.id, count: path.length };
	}).filter(e => e).sort((a, b) => a.count - b.count).pop();
	return top?.id;
}

export function getMainPage() {
	const pageList = Object.values(Store.get().appData.pages);
	const homePage = pageList.find(page => page.page === 'home') || pageList[0];
	return homePage ? homePage.id : null;
}

export function isVideosPage(id) {
	return Store.get().appData.pages[id]?.type === 'videos';
}

export function getPageFromVideoId(id) {
	const videoPages = Object.values(Store.get().appData.pages).filter(page => page.type === 'videos');
	const page = videoPages.find(page => Object.values(page.data?.list).find(video =>
		(video.youtubeId === id || video.vimeoId === id || video.videoSrc === id)
	));
	return page?.id || 'boltVideos';
}

export function shouldShowShowtimes(forceLive, goLiveDate) {
	if (!arguments.length) {
		const store = Store.get();
		forceLive = accessNested(store, 'appData.options.forceLive');
		goLiveDate = parseDate(accessNested(store, 'appData.meta.dates.live') || accessNested(store, 'appData.meta.dates.release'));
	}
	return forceLive || goLiveDate <= Date.now();
}

export const isDualViewEnabled = pages => {
	const appData = Store.get().appData;

	if (!appData?.options?.dualView) {
		return false;
	}

	if (!pages) {
		pages = Object.values(appData.pages);
	}

	const hasShowtimes = pages.find(page => getPageType(page) === 'showtimes');
	const hasHomeEnt = pages.find(page => getPageType(page) === 'homeEnt');
	const hasWidget = pages.find(page => getPageType(page) === 'stickAndScaleWidget');

	return (hasShowtimes && shouldShowShowtimes() && hasHomeEnt) || (hasShowtimes && hasHomeEnt && hasWidget);
};

export const getPageType = page => (
	page.component || page.type
);

export function addCSSRuleset(selector, declarations) {
	const style = document.createElement('style');
	const sheet = document.head.appendChild(style).sheet;

	if (Array.isArray(declarations)) {
		declarations = declarations.join(';');
	}
	if (declarations && typeof declarations === 'object') {
		declarations = Object.entries(declarations).map(([key, val]) => `${key}:${key === 'content' ? "'" + val + "'" : val}`).join(';');
	}
	sheet.insertRule(selector + '{' + declarations + '}', sheet.cssRules.length);
}

export function navigateInternally(destination, opts = {}) {
	if (destination.startsWith('@')) {
		return Store.emit('OPEN_RESERVED_VARIABLE_LINK', destination);
	}

	if (destination.includes('#') && !destination.startsWith('#')) {
		const pageAndHash = destination.match(/^([\w\d_-]+)#([\w\d_-]+)$/);
		if (pageAndHash?.length === 3) {
			Store.emit('SET_ACTIVE_PAGE', pageAndHash[1], null, { hashInPage: `#${pageAndHash[2]}` });
		}
		return;
	}

	const pages = Store.get().appData?.pages;
	let page = pages?.[destination];
	if (!page) {
		page = Object.values(pages || {}).find(e => e.path === destination || e.path === `/${destination}/`);
	}

	if (!page) {
		return;
	}

	if (!page.section && !page.type && destination !== 'boltHome') {
		Store.emit('CLOSE_ALL_OVERLAYS');
		Store.emit('SET_ACTIVE_PAGE', destination);
		return;
	}

	if (page?.type === 'showtimes') {
		Store.emit('SHOWTIMES_SHOW', opts?.animate, opts.queryString);
	} else if (page?.type === 'homeEnt') {
		Store.emit('HOMEENT_SHOW', undefined, undefined, destination);
	} else if (page?.type === 'pressKit') {
		Store.emit('PRESSKIT_SHOW', undefined, undefined, destination);
	} else if (page?.type === 'videos') {
		Store.emit('OPEN_VIDEO_OVERLAY', destination);
	} else {
		Store.emit('CLOSE_ALL_OVERLAYS');
		Store.emit('SET_ACTIVE_PAGE', destination);
		Store.emit('CHANGE_ACTIVE_SECTION', destination, { instant: opts?.instant !== false });
		if (page?.type === 'stickAndScaleWidget') {
			Store.emit('EXPAND_WIDGET');
		}
	}
}

const isObjectOrArray = v => typeof v === 'object' && v !== null;
function _merge(source, data) {
	if (!isObjectOrArray(data)) {
		return source;
	}
	Object.entries(data).forEach(([key, value]) => {
		if (!isObjectOrArray(value)) {
			source[key] = value;
			return;
		}
		source[key] = _merge(isObjectOrArray(source[key]) ? source[key] : {}, value);
	});
	return source;
}
function merge(source, ...objects) {
	return objects.reduce((merged, obj) => _merge(merged, obj), source);
}

export function getPageMeta({ store = Store.get(), appData = store.appData, page = store.activePage } = {}) {
	let metaTags = appData?.doc?.metaTags;
	let pageSpecificMeta = metaTags?.pageSpecificMetaTags?.[page];
	return merge({}, metaTags, pageSpecificMeta);
}

export function getPageTitle(data) {
	let store = data?.store || Store.get();
	let appData = data?.appData || store.appData;
	let page = data?.page || store.activePage;
	let meta = data?.meta || getPageMeta({ store, appData, page });
	let title = meta.title;
	let pageData = appData.pages?.[page];
	let pageName = pageData ? stringWithParams(appData.copy?.baseCopy, pageData.name) : '';
	return stringWithParams({ ...appData.copy?.baseCopy, PAGE_NAME: pageName }, title);
}

export function getHeaderHeight() {
	const header = document.querySelector('#header');
	if (!header) {
		return 0;
	}
	const headerHeight = getComputedStyle(header).height;
	return parseInt(headerHeight, 10);
}

export function linkOut(href, target) {
	const a = document.createElement('a');
	a.href = href;
	a.target = target || '_blank';
	a.rel = 'noopener';
	a.click();
}

/**
 * Pass it an array of objects and it removes the duplicate objects (if any) based on key.
 * e.g let array = [{key:"1", ...}, {key:"2", ...}, {key:"1", ...}]
 * removeDuplicates(array) will return [{key:"1"}, {key:"2"}]
 *
 * @param {Array.<object>} array
 * @returns {Array.<object>} array of objects with unique keys
*/
export function	arrayWithUniqueKeys(array, key = 'key') {
	let map = array.reduce((map, obj) => map.set(obj?.[key], obj), new Map());
	return [...map.values()];
}

export function getSectionsWithContent(sections) {
	if (typeof window === 'undefined') {
		return [];
	}
	return [...document.querySelectorAll('[data-section]')].filter(section => {
		return sections.find(s => s.id === section.getAttribute('data-section')) && section.hasChildNodes();
	});
}
